import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Download, Volume2 } from 'lucide-react';

const SpeechAudioPlayer = ({ blobUrl, filename = 'itriia-speech.mp3' }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    // Forcer le rechargement de la source audio
    if (audioRef.current && blobUrl) {
      audioRef.current.load();
    }
  }, [blobUrl]);

  const togglePlay = async () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (err) {
        console.error('[AudioPlayer] Play failed:', err);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  };

  const handleEnded = () => setIsPlaying(false);

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.click();
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px',
    }}>
      <audio
        ref={audioRef}
        src={blobUrl}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={(e) => console.error('[AudioPlayer] Error:', e.target.error)}
      />

      {/* Contrôles */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        {/* Play/Pause */}
        <button onClick={togglePlay} style={{
          width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #1D4ED8, #2563eb)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(29,78,216,0.35)', transition: 'transform 0.15s',
          flexShrink: 0,
        }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          {isPlaying ? <Pause size={18} /> : <Play size={18} style={{ marginLeft: 2 }} />}
        </button>

        {/* Barre de progression */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div onClick={handleSeek} style={{
            height: '6px', borderRadius: '3px', background: 'var(--input-bg)',
            cursor: 'pointer', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', borderRadius: '3px', width: `${progress}%`,
              background: 'linear-gradient(90deg, #1D4ED8, #3b82f6)',
              transition: 'width 0.1s linear',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)' }}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume icon */}
        <Volume2 size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />

        {/* Download */}
        <button onClick={handleDownload} title="Télécharger .mp3" style={{
          padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--card-border)',
          background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 500,
          transition: 'all 0.15s', flexShrink: 0,
        }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; e.currentTarget.style.color = '#3b82f6'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
        >
          <Download size={14} /> .mp3
        </button>
      </div>
    </div>
  );
};

export default SpeechAudioPlayer;
